import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/format';

/**
 * Toasts, primarily so a delete can be undone.
 *
 * The undo affordance is the reason this exists: deletion is soft server-side,
 * so "undo" restores the original row rather than creating a lookalike. A
 * confirmation dialog before every delete would be slower and worse.
 */

export interface Toast {
  id: number;
  message: string;
  tone: 'neutral' | 'error';
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  show: (
    message: string,
    options?: { tone?: Toast['tone']; action?: Toast['action']; duration?: number },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (message, { tone = 'neutral', action, duration = 5000 } = {}) => {
      const id = (nextId.current += 1);
      setToasts((current) => [
        ...current.slice(-2),
        { id, message, tone, ...(action ? { action } : {}) },
      ]);
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Polite, not assertive: a toast is a confirmation, and interrupting a
        // screen reader mid-sentence to say "deleted" is worse than waiting.
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-28 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-8"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'glass rise pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-floating px-4 py-3',
              toast.tone === 'error' && 'text-[var(--danger)]',
            )}
          >
            <span className="flex-1 text-[15px]">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action!.onClick();
                  dismiss(toast.id);
                }}
                className="press shrink-0 rounded-full px-3 py-1 text-[15px] font-semibold text-[var(--accent)]"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
